<template>
	<!--
		The tab strip is the reason this tree is interpreted rather than handed to <FormKitSchema>:
		AppFormTabs takes one named slot per tab, and FormKit's $cmp nodes cannot pass named slots.
		Both the `tabs` prop and the slot names come from the same children, so they cannot desync.
	-->
	<AppFormTabs
		v-if="node.type === 'tabs'"
		:tabs="tabTitles"
		:class="node.class"
	>
		<template
			v-for="tab in node.children"
			:key="tab.title"
			#[tab.title!]
		>
			<AppFormLayout v-bind="{ nodes: tab.children ?? [], schema, model }"/>
		</template>
	</AppFormTabs>

	<AppFieldSet
		v-else-if="node.type === 'fieldset'"
		:legend="node.title"
		:border="node.border ?? false"
		:class="node.class"
	>
		<AppFormLayout v-bind="{ nodes: node.children ?? [], schema, model }"/>
	</AppFieldSet>

	<AppColumns
		v-else-if="node.type === 'columns'"
		:count="node.count ?? 2"
		:classes="node.class ?? undefined"
	>
		<AppFormLayout v-bind="{ nodes: node.children ?? [], schema, model }"/>
	</AppColumns>

	<AppCollapse
		v-else-if="node.type === 'collapse'"
		:title="node.title ?? undefined"
		:class="node.class"
	>
		<AppFormLayout v-bind="{ nodes: node.children ?? [], schema, model }"/>
	</AppCollapse>

	<div
		v-else-if="node.type === 'group'"
		:class="node.class"
	>
		<AppFormLayout v-bind="{ nodes: node.children ?? [], schema, model }"/>
	</div>

	<AppAlert
		v-else-if="node.type === 'alert'"
		:type="node.alertType ?? 'info'"
	>
		{{ node.text }}
	</AppAlert>

	<span
		v-else-if="node.type === 'text'"
		:class="node.class"
	>
		{{ node.text }}
	</span>

	<AppSchemaInput
		v-else-if="node.type === 'field' && schema[node.name!]"
		:schema="schema[node.name!]"
		:class="node.class"
		v-bind="node.props ?? {}"
	/>

	<AppFieldSet
		v-else-if="node.type === 'customFields' && schema.customFields?.children?.length > 0"
		:legend="node.title"
	>
		<AppSchemaInput :schema="schema.customFields"/>
	</AppFieldSet>
</template>

<script setup lang="ts">
import { FormLayoutNode, FormLayoutNodeTypeEnum } from "@q-food/q-food";

/**
 * Renders one node of a server-defined layout. The `v-else-if` chain below is the whitelist of
 * node types: anything unrecognised renders nothing, so the backend can add a node type without
 * breaking an older client, and a compromised payload cannot conjure arbitrary components.
 */
const props = defineProps<{
	node: FormLayoutNode;
	schema: Record<string, any>;
	model: Record<string, any>;
}>();

// Deliberately hand-maintained rather than derived from FormLayoutNodeTypeEnum: this lists the
// types the template above actually renders, which is not the same as the types the contract
// allows. Typing it against the enum still catches a stale entry at compile time, while a type the
// backend adds and this component has no branch for correctly falls through to the warning below.
const KNOWN_TYPES = new Set<FormLayoutNodeTypeEnum>([
	"tabs",
	"fieldset",
	"columns",
	"collapse",
	"group",
	"alert",
	"text",
	"field",
	"customFields",
]);

const tabTitles = computed(() => (props.node.children ?? []).map((child) => child.title!).filter(Boolean));

// A node type this client does not know about renders nothing at all, which is silent in
// production and confusing in development — so say so once, loudly enough to notice.
if (!KNOWN_TYPES.has(props.node.type)) {
	console.warn(`[AppFormLayout] unknown layout node type "${props.node.type}"`, props.node);
}
</script>
